import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User, LoginCredentials, RegisterData, ApiResponse } from '../types'
import { api } from '../lib/api'
import toast from 'react-hot-toast'

type ViewMode = 'customer' | 'employee'

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (credentials: LoginCredentials) => Promise<void>
  register: (userData: RegisterData) => Promise<void>
  logout: () => void
  updateProfile: (userData: Partial<User>) => Promise<void>
  isAuthenticated: boolean
  isStoreOwner: boolean
  isDispatcher: boolean
  isChef: boolean
  isDelivery: boolean
  isPlatformAdmin: boolean
  hasStoreAccess: boolean
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  isEmployee: boolean
  isCustomer: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    // Cargar modo de vista desde localStorage o determinar por defecto
    const savedMode = localStorage.getItem('viewMode') as ViewMode | null
    if (savedMode && (savedMode === 'customer' || savedMode === 'employee')) {
      return savedMode
    }
    return 'customer' // Por defecto modo cliente
  })

  const setViewMode = (mode: ViewMode) => {
    setViewModeState(mode)
    localStorage.setItem('viewMode', mode)
  }

  useEffect(() => {
    // Verificar sesión existente
    const token = localStorage.getItem('token')
    const savedUser = localStorage.getItem('user')

    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser)
        // Normalizar usuario: mapear profile_photo_url a profilePhotoUrl
        const normalizedUser = {
          ...parsedUser,
          profilePhotoUrl: parsedUser.profilePhotoUrl || parsedUser.profile_photo_url || undefined
        }
        setUser(normalizedUser)
        
        // Si el usuario es empleado y no tiene modo guardado, establecer modo empleado por defecto
        const isEmployee = ['store_admin', 'dispatcher', 'chef', 'delivery'].includes(normalizedUser.type)
        if (isEmployee && !localStorage.getItem('viewMode')) {
          setViewMode('employee')
        }
      } catch (error) {
        console.error('Error parsing saved user:', error)
        localStorage.removeItem('token')
        localStorage.removeItem('user')
      }
    }

    setLoading(false)
  }, [])

  const login = async (credentials: LoginCredentials) => {
    try {
      setLoading(true)
      const response: ApiResponse<{ user: User; token: string }> = await api.login(credentials)

      if (response.success && response.data) {
        const { user, token } = response.data

        // Normalizar usuario: mapear profile_photo_url a profilePhotoUrl
        const normalizedUser = {
          ...user,
          profilePhotoUrl: (user as any).profilePhotoUrl || (user as any).profile_photo_url || undefined
        }

        localStorage.setItem('token', token)
        localStorage.setItem('user', JSON.stringify(normalizedUser))
        setUser(normalizedUser)

        toast.success(`¡Bienvenido ${normalizedUser.name}!`)
      } else {
        const errorMessage = response.message || 'Error al iniciar sesión. Verifica tus credenciales.'
        toast.error(errorMessage)
        throw new Error(errorMessage)
      }
    } catch (error: any) {
      // Extraer el mensaje de error del backend
      let errorMessage = 'Error al iniciar sesión. Verifica tus credenciales e intenta nuevamente.'
      
      if (error.response?.data?.message) {
        // Usar el mensaje específico del backend
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      } else if (error.response?.status === 401) {
        errorMessage = 'Credenciales incorrectas. Verifica tu email y contraseña.'
      } else if (error.response?.status === 0 || !error.response) {
        errorMessage = 'Error de conexión. Verifica tu conexión a internet e intenta nuevamente.'
      } else if (error.response?.status >= 500) {
        errorMessage = 'Error del servidor. Por favor intenta más tarde.'
      }
      
      // Siempre mostrar el mensaje de error en el login
      toast.error(errorMessage)
      
      throw new Error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const register = async (userData: RegisterData) => {
    try {
      setLoading(true)
      const response: ApiResponse<{ user: User; token: string }> = await api.register(userData)

      // Verificar si la respuesta es exitosa
      if (response && response.success && response.data) {
        const { user, token } = response.data

        // Normalizar usuario: mapear profile_photo_url a profilePhotoUrl
        const normalizedUser = {
          ...user,
          profilePhotoUrl: (user as any).profilePhotoUrl || (user as any).profile_photo_url || undefined
        }

        // Guardar datos antes de mostrar el mensaje de éxito
        localStorage.setItem('token', token)
        localStorage.setItem('user', JSON.stringify(normalizedUser))
        setUser(normalizedUser)

        // Mostrar mensaje de éxito
        toast.success('Cuenta creada exitosamente')
        
        // Retornar sin lanzar error
        return
      } else {
        // Si no hay success o data, lanzar error
        const errorMessage = response?.message || 'Error al crear cuenta'
        console.error('Error en registro - respuesta inválida:', response)
        throw new Error(errorMessage)
      }
    } catch (error: any) {
      console.error('Error completo en registro:', error)
      
      // Si el error ya tiene un mensaje del backend, no duplicar el toast
      // El interceptor de axios ya maneja algunos errores
      if (error.response?.data?.message) {
        // El interceptor ya mostró el toast, solo lanzar el error
        throw error
      }
      
      // Solo mostrar toast si no hay respuesta (error de red) o es error del servidor
      const errorMessage = error.message || 'Error al crear cuenta'
      if (!error.response || error.response.status >= 500) {
        toast.error(errorMessage)
      }
      
      throw error
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setUser(null)
    toast.success('Sesión cerrada')
  }

  const updateProfile = async (userData: Partial<User>) => {
    try {
      const response: ApiResponse<User> = await api.updateProfile(userData)

      if (response.success && response.data) {
        // Normalizar usuario: mapear profile_photo_url a profilePhotoUrl
        const updatedUser = {
          ...user!,
          ...response.data,
          profilePhotoUrl: (response.data as any).profilePhotoUrl || (response.data as any).profile_photo_url || user!.profilePhotoUrl
        }
        localStorage.setItem('user', JSON.stringify(updatedUser))
        setUser(updatedUser)
        toast.success('Perfil actualizado')
      } else {
        throw new Error(response.message || 'Error al actualizar perfil')
      }
    } catch (error: any) {
      toast.error(error.message || 'Error al actualizar perfil')
      throw error
    }
  }

  const isEmployee = ['store_admin', 'dispatcher', 'chef', 'delivery', 'store_owner'].includes(user?.type || '')
  const isCustomer = user?.type === 'customer' || !user

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile,
    isAuthenticated: !!user,
    isStoreOwner: user?.type === 'store_owner',
    isDispatcher: user?.type === 'dispatcher',
    isChef: user?.type === 'chef',
    isDelivery: user?.type === 'delivery',
    isPlatformAdmin: user?.type === 'platform_admin',
    hasStoreAccess: ['store_admin', 'dispatcher', 'chef', 'delivery'].includes(user?.type || ''),
    viewMode,
    setViewMode,
    isEmployee,
    isCustomer
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
